import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Upload, X } from 'lucide-react';
import type { Event } from '@/types/events';
import { useUpdateEvent } from '@/hooks/useEvents';

const imagesSchema = z.object({
  thumbnail: z.string().url('Must be a valid URL').optional().or(z.literal('')),
  inner_page_image: z.string().url('Must be a valid URL').optional().or(z.literal('')),
});

type ImagesFormData = z.infer<typeof imagesSchema>;

interface EventImagesInfoProps {
  event: Event;
  brandId: string;
}

export function EventImagesInfo({ event, brandId }: EventImagesInfoProps) {
  const updateEvent = useUpdateEvent();
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);

  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(event?.thumbnail || null);

  const [innerPageFile, setInnerPageFile] = useState<File | null>(null);
  const [innerPagePreview, setInnerPagePreview] = useState<string | null>(event?.inner_page_image || null);

  const form = useForm<ImagesFormData>({
    resolver: zodResolver(imagesSchema),
    defaultValues: {
      thumbnail: event?.thumbnail || '',
      inner_page_image: event?.inner_page_image || '',
    },
  });

  const onSubmit = async (data: ImagesFormData) => {
    setUploading(true);
    try {
      let finalThumbnail = data.thumbnail;
      if (thumbnailFile) {
        const fileExt = thumbnailFile.name.split('.').pop();
        const fileName = `event/${brandId}/${event.id}/thumbnail-${Date.now()}.${fileExt}`;
        const { error: uploadError } = await supabase.storage
          .from('course-thumbnails')
          .upload(fileName, thumbnailFile);
        if (uploadError) throw uploadError;
        const { data: { publicUrl } } = supabase.storage.from('course-thumbnails').getPublicUrl(fileName);
        finalThumbnail = publicUrl;
      }

      let finalInnerPage = data.inner_page_image;
      if (innerPageFile) {
        const fileExt = innerPageFile.name.split('.').pop();
        const fileName = `event/${brandId}/${event.id}/inner-${Date.now()}.${fileExt}`;
        const { error: uploadError } = await supabase.storage
          .from('course-thumbnails')
          .upload(fileName, innerPageFile);
        if (uploadError) throw uploadError;
        const { data: { publicUrl } } = supabase.storage.from('course-thumbnails').getPublicUrl(fileName);
        finalInnerPage = publicUrl;
      }

      updateEvent.mutate(
        {
          id: event.id,
          updates: {
            thumbnail: finalThumbnail || undefined,
            inner_page_image: finalInnerPage || undefined,
          },
        },
        {
          onSuccess: () => {
            setThumbnailFile(null);
            setInnerPageFile(null);
            toast({ title: 'Images updated successfully' });
          },
          onError: (err) => {
            toast({ title: 'Failed to update images', description: String(err), variant: 'destructive' });
          },
        }
      );
    } catch (err: unknown) {
      toast({
        title: 'Upload failed',
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Event Images</CardTitle>
        <CardDescription>Manage event thumbnail and inner page banner image</CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <div className="space-y-4">
              <FormLabel>Event Thumbnail</FormLabel>
              {thumbnailPreview && (
                <div className="relative w-full max-w-md">
                  <div className="aspect-video bg-muted rounded-lg overflow-hidden">
                    <img src={thumbnailPreview} alt="Event thumbnail" className="w-full h-full object-cover" />
                  </div>
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    className="absolute top-2 right-2"
                    onClick={() => {
                      setThumbnailFile(null);
                      setThumbnailPreview(null);
                      form.setValue('thumbnail', '');
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
              {!thumbnailPreview && (
                <div className="border-2 border-dashed rounded-lg p-8 text-center">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setThumbnailFile(file);
                        setThumbnailPreview(URL.createObjectURL(file));
                      }
                    }}
                    className="hidden"
                    id="event-thumbnail-upload"
                  />
                  <label htmlFor="event-thumbnail-upload" className="cursor-pointer">
                    <Upload className="mx-auto h-12 w-12 text-muted-foreground" />
                    <p className="mt-2 text-sm text-muted-foreground">Click to upload thumbnail</p>
                  </label>
                </div>
              )}
              <FormField
                control={form.control}
                name="thumbnail"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Or Enter Image URL</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="https://example.com/thumbnail.jpg"
                        {...field}
                        disabled={!!thumbnailFile}
                        onChange={(e) => {
                          field.onChange(e);
                          if (e.target.value) setThumbnailPreview(e.target.value);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="space-y-4 pt-6 border-t">
              <FormLabel>Inner Page Banner Image</FormLabel>
              {innerPagePreview && (
                <div className="relative w-full max-w-md">
                  <div className="aspect-video bg-muted rounded-lg overflow-hidden">
                    <img src={innerPagePreview} alt="Inner page banner" className="w-full h-full object-cover" />
                  </div>
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    className="absolute top-2 right-2"
                    onClick={() => {
                      setInnerPageFile(null);
                      setInnerPagePreview(null);
                      form.setValue('inner_page_image', '');
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
              {!innerPagePreview && (
                <div className="border-2 border-dashed rounded-lg p-8 text-center">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setInnerPageFile(file);
                        setInnerPagePreview(URL.createObjectURL(file));
                      }
                    }}
                    className="hidden"
                    id="event-inner-upload"
                  />
                  <label htmlFor="event-inner-upload" className="cursor-pointer">
                    <Upload className="mx-auto h-12 w-12 text-muted-foreground" />
                    <p className="mt-2 text-sm text-muted-foreground">Click to upload inner page banner</p>
                  </label>
                </div>
              )}
              <FormField
                control={form.control}
                name="inner_page_image"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Or Enter Image URL</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="https://example.com/banner.jpg"
                        {...field}
                        disabled={!!innerPageFile}
                        onChange={(e) => {
                          field.onChange(e);
                          if (e.target.value) setInnerPagePreview(e.target.value);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Button type="submit" disabled={uploading}>
              {uploading ? 'Uploading...' : 'Save Images'}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
